import { useQuery } from '@tanstack/react-query';
import { requestApi } from '~/services/api';
import { RecommendedGamesResponse, ResponseStatus } from './casino-recommended-types';
import { CasinoRecommendationsKey } from './casino-recommended-constants';
import { useSelector } from 'react-redux';
import { selectFranchiseCode } from '~/components/brand/ducks/selectors';
import { authenticationSelector } from '~/components/authentication/ducks/selectors';

const RECOMMENDED_GAMES_PATH = '/rest/state/casino/player/recommended';
const RECOMMENDED_GAMES_QUERY_KEY = 'recommended-games';

/**
 * Query recommended games, returned as RecommendedGame[]
 */
const useCasinoRecommendedGames = (recommendationsKey: CasinoRecommendationsKey, isEnabled: boolean) => {
	// Update on franchise code and authentication to ensure we only display compliant games.
	const franchiseCode = useSelector(selectFranchiseCode);
	const isAuthenticated = useSelector(authenticationSelector);

	const { isLoading, data: recommendedGames } = useQuery({
		queryKey: [RECOMMENDED_GAMES_QUERY_KEY, recommendationsKey, franchiseCode, isAuthenticated],
		enabled: isEnabled,
		gcTime: 15 * 60 * 1000,
		staleTime: 15 * 60 * 1000,
		refetchOnMount: true,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
		refetchInterval: false,
		queryFn: async () => {
			try {
				const response = (await requestApi(RECOMMENDED_GAMES_PATH)) as RecommendedGamesResponse;

				if (response.status !== ResponseStatus.SUCCESS) {
					throw new Error('Failed to fetch recommended games');
				}

				return response.result;
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('Failed to fetch recommended games', error);
			}

			return [];
		}
	});

	return {
		isLoading,
		recommendedGames: recommendedGames ?? []
	};
};
export default useCasinoRecommendedGames;
